/**
 * Credential Field
 *
 * Renders a credential selector with create button
 */

import { useState, useEffect } from 'react';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { credentialsApi } from '@/lib/api';
import { toast } from 'sonner';
import { CreateCredentialDialog } from '@/components/dialogs/CreateCredentialDialog';

interface CredentialFieldProps {
  property: InferredPropertySchema;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

interface Credential {
  id: string;
  name: string;
  type: string;
}

// Special marker for credentials that exist on a node but aren't accessible to the current user
// (e.g., workflow owner's credential on a shared workflow)
const SHARED_WORKFLOW_CREDENTIAL_MARKER = '__shared_workflow_credential__';
// Marker for credentials that no longer exist or have been deleted
const ORPHANED_CREDENTIAL_MARKER = '__orphaned_credential__';

export function CredentialField({
  property,
  value,
  onChange,
  error,
}: CredentialFieldProps) {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Track validation state for credentials not in the user's list
  const [credentialValidation, setCredentialValidation] = useState<{
    checked: boolean;
    exists: boolean;
    isDeleted: boolean;
  }>({ checked: false, exists: false, isDeleted: false });

  // Check if the current value is a credential that's not in the user's accessible list
  // This happens when viewing a shared workflow where the credential belongs to the owner
  const isUnknownCredential = value && !loading && credentials.length >= 0 &&
    !credentials.some(cred => cred.id === value);

  // Only show "Shared by workflow owner" if:
  // 1. The credential is not in user's list
  // 2. We've validated it exists
  // 3. It's not deleted
  const isSharedWorkflowCredential = isUnknownCredential &&
    credentialValidation.checked &&
    credentialValidation.exists &&
    !credentialValidation.isDeleted;

  // Show "Credential not found" if:
  // 1. The credential is not in user's list
  // 2. We've validated it doesn't exist OR it's deleted
  const isOrphanedCredential = isUnknownCredential &&
    credentialValidation.checked &&
    (!credentialValidation.exists || credentialValidation.isDeleted);

  // Get allowed credential types from property
  // credentialTypes is an array of CredentialReference objects with { name, required }
  // Also support typeOptions.credentialType (singular) for backward compatibility
  const credentialRefs = property.credentialTypes || [];
  const singleCredentialType = property.typeOptions?.credentialType;
  
  const allowedTypes = credentialRefs.length > 0
    ? credentialRefs.map((ref: any) => typeof ref === 'string' ? ref : ref.name)
    : singleCredentialType
    ? [singleCredentialType]
    : [];

  // Reload credentials when allowed types change (e.g., navigating between different node types)
  useEffect(() => {
    loadCredentials();
  }, [JSON.stringify(allowedTypes)]);

  // Validate credential existence when we detect an unknown credential
  useEffect(() => {
    const validateCredential = async () => {
      if (!isUnknownCredential || credentialValidation.checked) {
        return;
      }

      try {
        const response = await credentialsApi.checkExists(value);
        if (response.data) {
          setCredentialValidation({
            checked: true,
            exists: response.data.exists,
            isDeleted: response.data.isDeleted,
          });
        }
      } catch (error) {
        console.error('Failed to validate credential:', error);
        // On error, assume credential doesn't exist
        setCredentialValidation({
          checked: true,
          exists: false,
          isDeleted: false,
        });
      }
    };

    validateCredential();
  }, [isUnknownCredential, value, credentialValidation.checked]);

  // Reset validation state when value changes to a known credential
  useEffect(() => {
    if (!isUnknownCredential && credentialValidation.checked) {
      setCredentialValidation({ checked: false, exists: false, isDeleted: false });
    }
  }, [isUnknownCredential, credentialValidation.checked]);

  const loadCredentials = async () => {
    try {
      const response = await credentialsApi.list();
      // Handle different response structures
      let credentialsList: Credential[] = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          credentialsList = response.data;
        } else if (response.data.credentials && Array.isArray(response.data.credentials)) {
          credentialsList = response.data.credentials;
        }
      }
      // Filter credentials by allowed types
      const filtered = credentialsList.filter((cred: Credential) => 
        allowedTypes.length === 0 || allowedTypes.includes(cred.type)
      );
      setCredentials(filtered);
    } catch (error) {
      console.error('Failed to load credentials:', error);
      toast.error('Failed to load credentials');
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setCreateDialogOpen(true);
  };

  const handleCredentialCreated = async (credentialId: string) => {
    // Reload credentials list
    await loadCredentials();
    // Select the newly created credential if we have its ID
    if (credentialId) {
      onChange(credentialId);
    }
  };

  const getCredentialIcon = (type: string) => {
    switch (type) {
      case 'claude':
      case 'openai':
        return '🤖';
      case 'api_key':
      case 'bearer_token':
        return '🔑';
      case 'oauth2':
        return '🔐';
      default:
        return '🔒';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={property.key}>
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCreateNew}
          className="h-7 gap-1 text-xs"
        >
          <Plus className="h-3 w-3" />
          New
        </Button>
      </div>

      {property.description && (
        <p className="text-xs text-muted-foreground">{property.description}</p>
      )}

      <Select
        value={isSharedWorkflowCredential ? SHARED_WORKFLOW_CREDENTIAL_MARKER : isOrphanedCredential ? ORPHANED_CREDENTIAL_MARKER : (value || '')}
        onValueChange={(newValue) => {
          // Don't allow selecting the placeholder markers
          if (newValue !== SHARED_WORKFLOW_CREDENTIAL_MARKER && newValue !== ORPHANED_CREDENTIAL_MARKER) {
            onChange(newValue);
          }
        }}
        disabled={loading}
      >
        <SelectTrigger
          id={property.key}
          className={error || isOrphanedCredential ? 'border-destructive' : ''}
        >
          <SelectValue placeholder={loading ? 'Loading...' : 'Select credential'} />
        </SelectTrigger>
        <SelectContent>
          {/* Show orphaned credential indicator if applicable */}
          {isOrphanedCredential && (
            <SelectItem
              key={ORPHANED_CREDENTIAL_MARKER}
              value={ORPHANED_CREDENTIAL_MARKER}
              className="text-destructive"
            >
              <div className="flex items-center gap-2">
                <span>⚠️</span>
                <span>Credential not found</span>
              </div>
            </SelectItem>
          )}
          {/* Show shared workflow credential indicator if applicable */}
          {isSharedWorkflowCredential && (
            <SelectItem
              key={SHARED_WORKFLOW_CREDENTIAL_MARKER}
              value={SHARED_WORKFLOW_CREDENTIAL_MARKER}
              className="text-muted-foreground"
            >
              <div className="flex items-center gap-2">
                <span>🔗</span>
                <span>Shared by workflow owner</span>
              </div>
            </SelectItem>
          )}
          {credentials.length === 0 && !isSharedWorkflowCredential && !isOrphanedCredential ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No credentials available
            </div>
          ) : (
            credentials.map((cred) => (
              <SelectItem key={cred.id} value={cred.id}>
                <div className="flex items-center gap-2">
                  <span>{getCredentialIcon(cred.type)}</span>
                  <span>{cred.name}</span>
                  <span className="text-xs text-muted-foreground">({cred.type})</span>
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      {credentials.length === 0 && !loading && !isSharedWorkflowCredential && !isOrphanedCredential && (
        <p className="text-xs text-muted-foreground">
          No credentials found. Click "New" to create one.
        </p>
      )}

      {isOrphanedCredential && (
        <p className="text-xs text-destructive">
          The configured credential no longer exists or has been deleted. Please select a different credential.
        </p>
      )}

      {isSharedWorkflowCredential && (
        <p className="text-xs text-muted-foreground">
          This credential is managed by the workflow owner. You can use your own credential by selecting one from the list above.
        </p>
      )}

      <CreateCredentialDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleCredentialCreated}
        allowedTypes={allowedTypes}
      />
    </div>
  );
}
